import { apiV2Instance } from '../lib/api-v2.client';
import { forgetMetaMaskSessions } from './metamask-connect-storage';
import {
  isMobileWallet,
  loadMpWeb3,
  mountedMpWeb3,
  MpWeb3Api,
  MpWeb3State,
  sameAddress,
} from './mp-wallet';

export type WalletStatus =
  | 'restoring'
  | 'disconnected'
  | 'connecting'
  | 'unverified'
  | 'verifying'
  | 'connected'
  | 'disconnecting';

export interface WalletSnapshot {
  account: string | null;
  status: WalletStatus;
}

export type WalletErrorReason = 'conflict' | 'unproven' | 'save';

export class WalletError extends Error {
  readonly reason: WalletErrorReason;

  constructor(message: string, reason: WalletErrorReason) {
    super(message);
    this.name = 'WalletError';
    this.reason = reason;
  }
}

const sessionKey = 'quickcam_wallet_session';
const restoreGraceMs = 4000;
const walletDeadlineMs = 120 * 1000;

export function walletErrorMessage(error: any): string {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.data?.error?.message ||
    error?.data?.message ||
    error?.message ||
    'Failed to connect wallet.'
  );
}

function hasSession(): boolean {
  try {
    return localStorage.getItem(sessionKey) === '1';
  } catch {
    return false;
  }
}

function rememberSession(active: boolean): void {
  try {
    if (active) localStorage.setItem(sessionKey, '1');
    else localStorage.removeItem(sessionKey);
  } catch (error) {
    console.warn('Could not store the wallet session:', error);
  }
}

function readProfileWallet(): string {
  try {
    const raw = localStorage.getItem('persist:root');
    if (!raw) return '';
    const persist = JSON.parse(raw);
    const common = typeof persist.common === 'string' ? JSON.parse(persist.common) : persist.common;
    const value = String(common?.auth?.user?.walletAddress || '').trim().toLowerCase();
    return value === 'false' ? '' : value;
  } catch {
    return '';
  }
}

let snapshot: WalletSnapshot = {
  account: null,
  status: hasSession() ? 'restoring' : 'disconnected',
};
const listeners = new Set<(next: WalletSnapshot) => void>();
let generation = 0;

function update(next: WalletSnapshot): void {
  generation += 1;
  snapshot = next;
  listeners.forEach(listener => listener(snapshot));
}

export function getWalletSnapshot(): WalletSnapshot {
  return snapshot;
}

export function subscribeWallet(listener: (next: WalletSnapshot) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function isWalletProven(address: string): Promise<boolean> {
  try {
    const response = await apiV2Instance.get('/api/v2/user/wallet-verification', {
      params: { address },
      silent: true,
    } as any);
    return Boolean(response.data?.data?.verified);
  } catch {
    return false;
  }
}

async function saveWallet(address: string): Promise<void> {
  let response;
  try {
    response = await apiV2Instance.put('/api/v2/user/update-wallet-address', {
      walletAddress: address,
    });
  } catch (error) {
    throw new WalletError(walletErrorMessage(error), 'save');
  }
  if (response.data?.success !== true) {
    throw new WalletError(walletErrorMessage(response.data), 'save');
  }
}

function accept(address: string): void {
  rememberSession(true);
  update({ account: address, status: 'connected' });
}

function withDeadline<T>(work: Promise<T>, onTimeout: () => Error): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(onTimeout()), walletDeadlineMs);
  });
  return Promise.race([work, deadline]).finally(() => clearTimeout(timer));
}

async function acceptIfProven(address: string, valid: () => boolean): Promise<boolean> {
  if (readProfileWallet() === address.toLowerCase()) {
    if (valid()) accept(address);
    return true;
  }
  const proven = await isWalletProven(address);
  if (!valid()) return true;
  if (!proven) return false;
  await saveWallet(address);
  if (valid()) accept(address);
  return true;
}

const busy: WalletStatus[] = ['connecting', 'verifying', 'disconnecting'];

async function onWalletState(state: MpWeb3State): Promise<void> {
  if (busy.includes(snapshot.status) || state.isConnecting) return;
  if (!state.isConnected || !state.address) {
    if (snapshot.status !== 'restoring' && snapshot.status !== 'disconnected') {
      update({ account: null, status: 'disconnected' });
    }
    return;
  }
  if (snapshot.status === 'connected' && sameAddress(state.address, snapshot.account)) return;
  const { address } = state;
  const started = generation;
  const valid = () => generation === started
    && sameAddress(mountedMpWeb3()?.getState().address, address);
  try {
    if (await acceptIfProven(address, valid)) return;
  } catch (error) {
    console.error('Could not save the wallet:', error);
  }
  if (valid()) update({ account: null, status: 'unverified' });
}

let unwatch: (() => void) | null = null;

function watch(api: MpWeb3Api): void {
  if (unwatch) return;
  unwatch = api.subscribe(state => {
    onWalletState(state).catch(error => console.error('Wallet state update failed:', error));
  });
}

let restoring: Promise<void> | null = null;

async function restore(): Promise<void> {
  if (!hasSession()) {
    if (snapshot.status === 'restoring') update({ account: null, status: 'disconnected' });
    return;
  }
  try {
    const api = await loadMpWeb3();
    watch(api);
    await new Promise(resolve => setTimeout(resolve, restoreGraceMs));
    if (snapshot.status === 'restoring') update({ account: null, status: 'disconnected' });
  } catch (error) {
    console.error('Could not restore the wallet connection:', error);
    if (snapshot.status === 'restoring') update({ account: null, status: 'disconnected' });
  }
}

export function restoreWallet(): Promise<void> {
  if (!restoring) restoring = restore();
  return restoring;
}

export function preloadWallet(): void {
  loadMpWeb3()
    .then(watch)
    .catch(error => console.error('Could not load the wallet:', error));
}

async function proveWith(api: MpWeb3Api, address: string, proving: Promise<string>): Promise<string | null> {
  const result = await proving;
  if (result === 'conflict') {
    await api.disconnect().catch(() => undefined);
    rememberSession(false);
    update({ account: null, status: 'disconnected' });
    throw new WalletError('This wallet is already linked to another QuickCam account.', 'conflict');
  }
  if (result !== 'proven') {
    update({ account: null, status: 'unverified' });
    throw new WalletError('Wallet ownership verification failed or was declined. Please try again.', 'unproven');
  }
  await saveWallet(address);
  if (snapshot.status !== 'verifying' && snapshot.status !== 'unverified') return null;
  accept(address);
  return address;
}

let pending: Promise<string | null> | null = null;

function once(run: () => Promise<string | null>): Promise<string | null> {
  if (!pending) {
    pending = run().finally(() => {
      pending = null;
    });
  }
  return pending;
}

export function verifyWallet(): Promise<string | null> {
  return once(async () => {
    const api = mountedMpWeb3();
    const address = api?.getState().address;
    if (!api || !address) {
      update({ account: null, status: 'disconnected' });
      return null;
    }
    update({ account: null, status: 'verifying' });
    const proving = api.prove();
    try {
      return await withDeadline(proveWith(api, address, proving), () => {
        if (snapshot.status === 'verifying') update({ account: null, status: 'unverified' });
        return new WalletError(
          'MetaMask did not answer the signature request. Tap Verify Wallet to try again.',
          'unproven',
        );
      });
    } catch (error) {
      if (snapshot.status === 'verifying') update({ account: null, status: 'unverified' });
      throw error;
    }
  });
}

export function connectWallet({ chainId }: { chainId?: number } = {}): Promise<string | null> {
  if (snapshot.status === 'unverified') return verifyWallet();
  return once(async () => {
    if (snapshot.status === 'connected' && snapshot.account) return snapshot.account;
    if (snapshot.status === 'disconnecting') return null;
    update({ account: null, status: 'connecting' });
    const started = generation;
    const valid = () => generation === started;
    try {
      const loaded = mountedMpWeb3();
      const connecting = loaded ? loaded.connect(chainId ? { chainId } : undefined) : null;
      const api = loaded || (await loadMpWeb3());
      const state = await withDeadline(
        connecting || api.connect(),
        () => new Error(isMobileWallet()
          ? 'MetaMask did not answer the connection request. Close MetaMask fully (swipe it away), open it again, then try again.'
          : 'MetaMask did not answer the connection request. Please try again.'),
      );
      watch(api);
      const { address } = state;
      if (!address) throw new Error('The wallet did not return an account.');
      if (!valid()) return null;
      rememberSession(true);
      if (await acceptIfProven(address, valid)) {
        return snapshot.status === 'connected' ? address : null;
      }
      if (!valid()) return null;
      if (isMobileWallet()) {
        update({ account: null, status: 'unverified' });
        return null;
      }
      update({ account: null, status: 'verifying' });
      return await proveWith(api, address, api.prove());
    } catch (error) {
      if (snapshot.status === 'connecting' || snapshot.status === 'verifying') {
        update({ account: null, status: 'disconnected' });
      }
      throw error;
    }
  });
}

export async function disconnectWallet(): Promise<void> {
  const previous = snapshot;
  update({ account: previous.account, status: 'disconnecting' });
  try {
    if (window.mpWeb3) await window.mpWeb3.disconnect();
  } catch (error) {
    await forgetMetaMaskSessions();
    update(previous);
    throw error;
  }
  await forgetMetaMaskSessions();
  rememberSession(false);
  update({ account: null, status: 'disconnected' });
}

export function resetWalletSessionForTests(): void {
  snapshot = { account: null, status: hasSession() ? 'restoring' : 'disconnected' };
  listeners.clear();
  unwatch = null;
  restoring = null;
  pending = null;
}
